import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Form, Button, Spinner, Alert } from "react-bootstrap";
import { FaEdit, FaTrash, FaPlus, FaEye, FaFileInvoiceDollar } from "react-icons/fa";
import { Link } from "react-router-dom";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../Components/Pagination";
import api from "../services/api";
import "./CustomerList.css";

const ProformaList = () => {
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [proformas, setProformas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState({
    total: 0,
    accepted: 0,
    sent: 0,
    draft: 0
  });

  // Status class function
  const getStatusClass = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "draft") return "status-badge status-pending";
    if (s === "sent") return "status-badge status-progress";
    if (s === "accepted") return "status-badge status-approved";
    if (s === "rejected") return "status-badge status-rejected";
    return "status-badge status-default";
  };

  // Fetch proformas from API
  const fetchProformas = async (filters = {}) => {
    try {
      setLoading(true);
      setError("");
      const response = await api.post("/proforma_list.php", filters);
      
      if (response.data.head.code === 200) {
        const proformaData = response.data.body || [];
        setProformas(proformaData);
        
        // Calculate stats
        const stats = {
          total: proformaData.length,
          accepted: proformaData.filter(p => p.status === 'Accepted').length,
          sent: proformaData.filter(p => p.status === 'Sent').length,
          draft: proformaData.filter(p => p.status === 'Draft').length
        };
        setStats(stats);
      } else {
        setError(response.data.head.msg || "Failed to fetch proformas");
        setProformas([]);
      }
    } catch (err) {
      console.error("Error fetching proformas:", err);
      setError("Failed to load proformas. Please try again.");
      setProformas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProformas();
  }, []);

  // Handle filter changes
  useEffect(() => {
    const filters = {};
    if (search) filters.search = search;
    if (fromDate) filters.from_date = fromDate;
    if (toDate) filters.to_date = toDate;
    if (statusFilter) filters.status = statusFilter;
    
    // Don't fetch on every filter change, only on button click or implement debounce
  }, [search, fromDate, toDate, statusFilter]);

  const applyFilters = () => {
    const filters = {};
    if (search) filters.search = search;
    if (fromDate) filters.from_date = fromDate;
    if (toDate) filters.to_date = toDate;
    if (statusFilter) filters.status = statusFilter;
    
    fetchProformas(filters);
  };

  // Delete proforma handler
  const handleDelete = async (id) => {
    if (!window.confirm(`Are you sure you want to delete proforma #${id}?`)) {
      return;
    }

    try {
      const response = await api.post("/proforma_delete.php", { id });
      if (response.data.head.code === 200) {
        alert("Proforma deleted successfully!");
        fetchProformas(); // Refresh the list
      } else {
        alert(response.data.head.msg || "Failed to delete proforma");
      }
    } catch (err) {
      console.error("Error deleting proforma:", err);
      alert("Failed to delete proforma");
    }
  };

  // Filter proformas locally for display
  const filteredProformas = proformas.filter((proforma) => {
    if (!search && !fromDate && !toDate && !statusFilter) return true;
    
    const query = search.toLowerCase();
    const matchesSearch = 
      String(proforma.id).toLowerCase().includes(query) ||
      (proforma.quotation_id && proforma.quotation_id.toLowerCase().includes(query)) ||
      (proforma.customer_name && proforma.customer_name.toLowerCase().includes(query)) ||
      (proforma.customer_phone && proforma.customer_phone.includes(query)) ||
      (proforma.grand_total && proforma.grand_total.toString().toLowerCase().includes(query));

    // Date range filtering
    const proformaDate = new Date(proforma.date);
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;

    const matchesDateRange = 
      (!from || proformaDate >= from) && 
      (!to || proformaDate <= to);

    // Status filtering
    const matchesStatus = !statusFilter || proforma.status === statusFilter;

    return matchesSearch && matchesDateRange && matchesStatus;
  });

  // Pagination hook
  const {
    currentPage,
    totalPages,
    currentItems: currentProformas,
    handlePageChange,
    indexOfFirstItem
  } = usePagination(filteredProformas, 10);

  const clearDateFilter = () => {
    setFromDate("");
    setToDate("");
  };

  const clearStatusFilter = () => {
    setStatusFilter("");
  };

  const clearAllFilters = () => {
    setFromDate("");
    setToDate("");
    setStatusFilter("");
    setSearch("");
    fetchProformas(); // Fetch without filters
  };

  const calculateTotalAmount = () => {
    return filteredProformas.reduce((total, proforma) => {
      const amount = parseFloat(proforma.grand_total) || 0;
      return total + amount;
    }, 0);
  };

  // Format ID as PRO-{id}
  const formatProformaId = (id) => {
    return `PRO-${id}`;
  };

  if (loading && proformas.length === 0) {
    return (
      <Container fluid className="outer-container my-3">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading proformas...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="outer-container my-3">
      <h2 className="text-center mb-3">Proforma Invoice Management</h2>
      
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {/* Filters Section */}
      <div className="filters-section mb-4 p-3 border rounded bg-light">
        <Row className="g-3">
          {/* Date Range Filters */}
          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>From Date:</Form.Label>
              <Form.Control
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>To Date:</Form.Label>
              <Form.Control
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Form.Group>
          </Col>
          
          {/* Status Filter */}
          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>Status:</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="Draft">Draft</option>
                <option value="Sent">Sent</option>
                <option value="Accepted">Accepted</option>
                <option value="Rejected">Rejected</option>
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Filter Actions */}
          <Col xs={12} md={6} lg={3} className="d-flex align-items-end gap-2">
            <Button 
              variant="primary" 
              onClick={applyFilters}
              size="sm"
            >
              Apply Filters
            </Button>
            <Button 
              variant="outline-secondary" 
              onClick={clearAllFilters}
              size="sm"
            >
              Clear All
            </Button>
          </Col>
        </Row>
      </div>

      {/* Search and Add Proforma Section */}
      <Row className="mb-3 align-items-center">
        <Col xs={12} md={6} lg={4} className="mb-2 mb-md-0">
          <Form.Control
            placeholder="Search by ID, customer, or amount"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
          />
        </Col>

        <Col xs={12} md={6} lg={8} className="d-flex justify-content-md-end">
          <Link
            to="/addproforma"
            className="btn-top d-inline-flex align-items-center"
          >
            <FaPlus className="me-2" /> Add Proforma
          </Link>
        </Col>
      </Row>

      {/* Stats Summary */}
      <Row className="mb-4">
        <Col xs={6} md={3}>
          <div className="stat-card text-center p-3 border rounded bg-success text-white">
            <h4 className="mb-1 me-2">{stats.accepted}</h4>
            <small>Accepted</small>
          </div>
        </Col>
        <Col xs={6} md={3}>
          <div className="stat-card text-center p-3 border rounded bg-info text-white">
            <h4 className="mb-1 me-2">{stats.sent}</h4>
            <small>Sent</small>
          </div>
        </Col>
        <Col xs={6} md={3}>
          <div className="stat-card text-center p-3 border rounded bg-warning text-dark">
            <h4 className="mb-1 me-2">{stats.draft}</h4>
            <small>Draft</small>
          </div>
        </Col>
        <Col xs={6} md={3}>
          <div className="stat-card text-center p-3 border rounded bg-primary text-white">
            <h4 className="mb-1 me-2">{stats.total}</h4>
            <small>Total Proformas</small>
          </div>
        </Col>
      </Row>

      {/* Table Section */}
      <div className="table-pagination-wrapper">
        <div className="table-pagination-content">
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
              <p className="mt-2">Loading...</p>
            </div>
          ) : (
            <Table bordered responsive className="table-striped customer-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Proforma ID</th>
                  <th>Date</th>
                  <th>Customer Name</th>
                  <th>Phone Number</th>
                  <th className="right-align">Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentProformas.map((proforma, index) => (
                  <tr key={proforma.id}>
                    <td>{indexOfFirstItem + index + 1}</td>
                    <td>
                      <div className="d-flex align-items-center">
                        <FaFileInvoiceDollar className="text-primary me-2" />
                        {formatProformaId(proforma.id)}
                        {proforma.quotation_id && (
                          <small className="text-muted ms-2">(Ref: {proforma.quotation_id})</small>
                        )}
                      </div>
                    </td>
                    <td>{proforma.date}</td>
                    <td>{proforma.customer_name || "N/A"}</td>
                    <td>{proforma.customer_phone || "N/A"}</td>
                    <td className="fw-bold right-align">₹{parseFloat(proforma.grand_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td>
                      <span className={getStatusClass(proforma.status)}>
                        {proforma.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-icons">
                        <Link
                          to={`/viewproforma/${proforma.id}`}
                          className="view-link"
                          title="View Proforma"
                        >
                          <FaEye className="view-icon" />
                        </Link>

                        <Link
                          to={`/editproforma/${proforma.id}`}
                          className="me-2"
                          title="Edit Proforma"
                        >
                          <FaEdit className="edit-icon" />
                        </Link>
                        <FaTrash
                          className="delete-icon"
                          title="Delete Proforma"
                          onClick={() => handleDelete(proforma.id)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                {currentProformas.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      <div className="text-muted">
                        <FaFileInvoiceDollar size={48} className="mb-3" />
                        <p>No proforma invoices found</p>
                        <small>Try adjusting your search or filters</small>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {/* Summary Footer */}
      <Row className="mt-4">
        <Col xs={12}>
          <div className="summary-footer p-3 border rounded bg-light">
            <Row>
              <Col md={4}>
                <strong>Total Proformas:</strong> {filteredProformas.length}
              </Col>
              <Col md={4}>
                <strong>Total Amount:</strong> ₹{calculateTotalAmount().toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </Col>
              <Col md={4}>
                <strong>Filtered Results:</strong> {filteredProformas.length} of {proformas.length}
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default ProformaList;